import React, { useContext, useState, useEffect, useCallback, memo, useRef } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { NotificationContext } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { auth } from '../utils/firebaseConfig';
import { presenceTracker } from '../utils/presenceTracker';
import logo from '../assets/logo.png';
import {
  Home,
  User,
  FileText,
  PlusCircle,
  Settings,
  LogOut,
  LogIn,
  UserPlus,
  Search,
  Bell,
  Star
} from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmationModal from './ConfirmationModal';

import { FiFileText, FiShield, FiCalendar } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// Navigation configuration
const navItems = [
  { to: '/', Icon: Home, label: 'Home', roles: ['all'], ariaLabel: 'Navigate to Home' },
  { to: '/profile', Icon: User, label: 'Profile', roles: ['all'], ariaLabel: 'View Profile' },
  { to: '/search-reports', Icon: Search, label: 'Search Reports', roles: ['all'], ariaLabel: 'Search Reports' },
  { to: '/dors', Icon: FileText, label: 'DORs', roles: ['trainer', 'admin'], ariaLabel: 'View DORs' },
  { id: 'create-report-toggle', Icon: PlusCircle, label: 'New Report', roles: ['trainer', 'admin'], ariaLabel: 'Open create report menu' },
  { to: '/settings', Icon: Settings, label: 'Settings', roles: ['admin'], ariaLabel: 'Admin Settings' },
  { id: 'alerts-toggle', Icon: Bell, label: 'Alerts', roles: ['all'], ariaLabel: 'View Notifications' },
];

const reportTypesForDropdown = [
  { path: '/create-dor', Icon: FiFileText, label: 'Daily Observation' },
  { path: '/create-officer-safety-report', Icon: FiShield, label: 'Officer Safety' },
  { path: '/create-end-of-week-report', Icon: FiCalendar, label: 'End of Week' },
];

// Enhanced Tooltip Component with modern styling
const Tooltip = ({ text }) => {
  const isSettingsTooltip = text === 'Settings';
  const displayText = isSettingsTooltip ? 'Settings (Admin Only)' : text;
  
  return (
    <motion.span 
      initial={{ opacity: 0, x: -10, scale: 0.9 }}
      whileHover={{ opacity: 1, x: 0, scale: 1 }}
      className="absolute left-full top-1/2 transform -translate-y-1/2 ml-3
        px-3 py-2 bg-primary-dark dark:bg-primary-light text-primary-light dark:text-primary-dark text-sm rounded-lg
        opacity-0 group-hover:opacity-100 hidden lg:block transition-all duration-200 delay-300 
        shadow-xl border border-secondary-dark dark:border-secondary-light z-50 whitespace-nowrap
        before:absolute before:right-full before:top-1/2 before:transform before:-translate-y-1/2
        before:border-4 before:border-transparent before:border-r-primary-dark dark:before:border-r-primary-light">
      {displayText}
      {isSettingsTooltip && (
        <Star className="inline-block ml-1 text-yellow-400" size={12} fill="currentColor" />
      )}
    </motion.span>
  );
};

const Sidebar = memo(() => {
  const { currentUser, role } = useContext(AuthContext);
  const { unreadCount, toggleModal } = useContext(NotificationContext);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showCreateReportDropdown, setShowCreateReportDropdown] = useState(false);
  const [dropdownPositionStyle, setDropdownPositionStyle] = useState({});
  const createReportButtonRef = useRef(null);
  const createReportDropdownRef = useRef(null);

  const handleSignOutConfirmed = async () => {
    try {
      await presenceTracker.cleanup().catch(err => {
        console.warn('Presence cleanup failed:', err);
      });
      await auth.signOut();
      toast.success('Signed out successfully!');
      navigate('/login');
    } catch (error) {
      console.error('Error during sign out:', error);
      toast.error('Sign out failed. Please try again.');
    } finally {
      setShowSignOutModal(false);
    }
  };

  const handleOpenSignOutModal = () => setShowSignOutModal(true);

  const handleCloseSignOutModal = () => setShowSignOutModal(false);

  // Enhanced style variables with modern glassmorphism and micro-interactions
  const linkBaseClasses = `
    flex items-center justify-center py-3 w-full rounded-xl my-0.5
    text-text-light dark:text-text-dark
    transition-all duration-300 ease-out
    hover:bg-gradient-to-r hover:from-accent-light/20 hover:to-accent-light/10 
    dark:hover:from-accent-dark/20 dark:hover:to-accent-dark/10
    hover:text-accent-light dark:hover:text-accent-dark
    hover:shadow-lg hover:scale-105
    relative group backdrop-blur-sm overflow-hidden
    focus:outline-none focus:ring-2 focus:ring-accent-light/50 dark:focus:ring-accent-dark/50 focus:ring-offset-2
    active:scale-95 active:transition-transform active:duration-75
  `;
  const activeClasses = `
    text-accent-light dark:text-accent-dark bg-gradient-to-r from-accent-light/30 to-accent-light/20
    dark:from-accent-dark/30 dark:to-accent-dark/20 shadow-lg scale-105
    border border-accent-light/30 dark:border-accent-dark/30
  `;

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (showCreateReportDropdown && createReportButtonRef.current) {
      const rect = createReportButtonRef.current.getBoundingClientRect();
      setDropdownPositionStyle({
        top: rect.top, // Align top with button (both are fixed context)
        left: rect.right + 5, // Position to the right of the button, +5px gap (scrollX not needed for fixed)
      });
    }
  }, [showCreateReportDropdown]);

  // Click outside handler for create report dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (createReportDropdownRef.current && !createReportDropdownRef.current.contains(event.target) &&
          createReportButtonRef.current && !createReportButtonRef.current.contains(event.target)) {
        setShowCreateReportDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [createReportButtonRef, createReportDropdownRef]);

  const renderNavItem = useCallback((itemOrPath, IconComponent, labelText, ariaLabelText) => {
    let to, id, Icon, label, ariaLabel;

    if (typeof itemOrPath === 'string') {
      to = itemOrPath; Icon = IconComponent; label = labelText; ariaLabel = ariaLabelText; id = null;
    } else {
      ({ to, id, Icon, label, ariaLabel } = itemOrPath);
    }

    const isCreateReportToggle = id === 'create-report-toggle';
    const isAlertsToggle = id === 'alerts-toggle';
    const badgeCount = isAlertsToggle ? unreadCount : 0;

    // Check if this is the settings item for admin indicator
    const isSettingsItem = to === '/settings';
    
    const content = (
      <>
        <div className="relative">
          <Icon className="flex-shrink-0 transition-all duration-300 group-hover:scale-125 group-hover:rotate-12" size={18} />
          {isAlertsToggle && badgeCount > 0 && (
            <span 
              className="absolute -top-1 -right-1 inline-flex items-center justify-center p-0.5 px-1 
                         rounded-full text-[8px] font-bold bg-gradient-to-r from-red-500 to-red-600 
                         text-white min-w-[16px] h-[16px] leading-none shadow-lg
                         border-2 border-primary-light dark:border-primary-dark"
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
          {/* Admin indicator for Settings */}
          {isSettingsItem && (
            <div className="absolute -top-1 -right-1 inline-flex items-center justify-center
                            rounded-full text-[7px] font-bold bg-gradient-to-r from-yellow-400 to-yellow-500 
                            text-yellow-900 min-w-[10px] h-[10px] leading-none shadow-lg
                            border border-yellow-300">
              <Star size={8} fill="currentColor" />
            </div>
          )}
          {/* Add subtle activity indicator */}
          <motion.div
            className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-400 rounded-full opacity-0 group-hover:opacity-100
                       transition-opacity duration-300 shadow-sm"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
        <Tooltip text={label} />
      </>
    );

    if (isCreateReportToggle) {
      return (
        <button ref={createReportButtonRef} type="button" onClick={() => setShowCreateReportDropdown(prev => !prev)} className={`${linkBaseClasses} ${showCreateReportDropdown ? activeClasses : ''} w-full`} aria-label={ariaLabel} aria-expanded={showCreateReportDropdown}>
          {content}
        </button>
      );
    }
    if (isAlertsToggle) {
      return <button type="button" onClick={toggleModal} className={linkBaseClasses} aria-label={ariaLabel}>{content}</button>;
    }
    if (to) {
      return <NavLink to={to} className={({ isActive }) => `${linkBaseClasses} ${isActive ? activeClasses : ''}`} aria-label={ariaLabel}>{content}</NavLink>;
    }
    return null;
  }, [linkBaseClasses, activeClasses, toggleModal, unreadCount, showCreateReportDropdown, navigate, createReportButtonRef, createReportDropdownRef, setShowCreateReportDropdown]);

  // Modern sidebar with glassmorphism and enhanced visual hierarchy
  const sidebarClasses = `
    fixed top-0 left-0 h-screen w-20
    bg-gradient-to-b from-primary-light/95 to-secondary-light/95 dark:from-primary-dark/95 dark:to-secondary-dark/95
    backdrop-blur-xl border-r border-secondary-light/50 dark:border-secondary-dark/50
    flex flex-col items-center shadow-2xl shadow-gray-900/10 dark:shadow-black/30
    transition-all duration-300
  `;

  return (
    <>
      <aside className={sidebarClasses}>
        {/* Enhanced Logo Section with modern styling */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-4 w-full flex justify-center border-b border-secondary-light/30 dark:border-secondary-dark/30 mb-4"
        >
          <Link to="/" aria-label="Go to Home" className="group">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              className="relative p-2 rounded-2xl bg-gradient-to-br from-accent-light/20 to-accent-light/10 
                         dark:from-accent-dark/20 dark:to-accent-dark/10 backdrop-blur-sm
                         group-hover:shadow-lg group-hover:shadow-accent-light/25 dark:group-hover:shadow-accent-dark/25
                         transition-all duration-300"
            >
              <img 
                src={logo} 
                alt="Logo" 
                className="w-8 h-8 filter transition-all duration-300" 
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent-light/10 to-transparent 
                              dark:from-accent-dark/10 dark:to-transparent
                              opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          </Link>
        </motion.div>

        {/* Enhanced Navigation with staggered animations */}
        <nav className="flex-1 flex flex-col space-y-1 w-full overflow-y-auto overflow-x-visible px-2 py-2">
          {currentUser ? (
            <>
              {navItems
                .filter(item => item.roles.includes('all') || item.roles.includes(role))
                .map((item, index) => (
                  <motion.div
                    key={item.to || item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.2, duration: 0.4 }}
                  >
                    {renderNavItem(item)}
                  </motion.div>
                ))}
              <div className="mt-auto" /> 
              {/* Enhanced Sign Out Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                className="px-2 pb-4 pt-3 border-t border-secondary-light/30 dark:border-secondary-dark/30 mt-auto"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleOpenSignOutModal}
                  className={`${linkBaseClasses} bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-800/30 
                             text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300
                             border border-red-200/50 dark:border-red-700/50`}
                  aria-label="Sign Out"
                >
                  <LogOut className="flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" size={18} />
                  <Tooltip text="Sign Out" />
                </motion.button>
              </motion.div>
            </>
          ) : (
            <>
              {renderNavItem('/login', LogIn, 'Sign In', 'Navigate to Sign In')}
              {renderNavItem('/register', UserPlus, 'Register', 'Navigate to Register')}
            </>
          )}
        </nav>
      </aside>

      <AnimatePresence>
        {showCreateReportDropdown && (
          <motion.div 
            ref={createReportDropdownRef}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`fixed w-64 bg-primary-light/95 dark:bg-primary-dark/95 backdrop-blur-xl 
                       border border-secondary-light/50 dark:border-secondary-dark/50 rounded-2xl 
                       shadow-2xl shadow-gray-900/20 dark:shadow-black/40 z-50 py-3 overflow-hidden`}
            style={dropdownPositionStyle}
            role="menu"
          >
            {/* Dropdown header */}
            <div className="px-4 pb-2 border-b border-secondary-light/30 dark:border-secondary-dark/30">
              <h3 className="text-sm font-semibold text-text-light dark:text-text-dark flex items-center">
                <PlusCircle className="w-4 h-4 mr-2 text-accent-light dark:text-accent-dark" />
                Create New Report
              </h3>
            </div>
            
            {/* Report options */}
            <div className="py-2">
              {reportTypesForDropdown.map((report, index) => (
                <motion.button
                  key={report.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                  onClick={() => { navigate(report.path); setShowCreateReportDropdown(false); }}
                  className="w-full text-left px-4 py-3 text-sm text-text-light dark:text-text-dark 
                           hover:bg-gradient-to-r hover:from-accent-light/20 hover:to-accent-light/10
                           dark:hover:from-accent-dark/20 dark:hover:to-accent-dark/10
                           hover:text-accent-light dark:hover:text-accent-dark
                           flex items-center group transition-all duration-200 mx-2 rounded-xl"
                  role="menuitem"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg 
                                  bg-secondary-light dark:bg-secondary-dark group-hover:bg-accent-light/20 
                                  dark:group-hover:bg-accent-dark/20 mr-3 transition-all duration-200">
                    <report.Icon className="w-4 h-4 text-text-light dark:text-text-dark 
                                           group-hover:text-accent-light dark:group-hover:text-accent-dark
                                           transition-colors duration-200" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium">{report.label}</span>
                    <div className="text-xs text-text-light/70 dark:text-text-dark/70 mt-0.5">
                      {report.path === '/create-dor' && 'Daily performance tracking'}
                      {report.path === '/create-officer-safety-report' && 'Safety incident documentation'}
                      {report.path === '/create-end-of-week-report' && 'Weekly progress summary'}
                    </div>
                  </div>
                  <motion.div
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    whileHover={{ x: 3 }}
                  >
                    <svg className="w-4 h-4 text-accent-light dark:text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={showSignOutModal}
        onClose={handleCloseSignOutModal}
        onConfirm={handleSignOutConfirmed}
        title="Confirm Sign Out"
        message="Are you sure you want to sign out?"
      />
    </>
  );
});

export default Sidebar;